import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

import { editPaymentMethodSuccess as PaymentMethodActionsEditPaymentMethodSuccess } from '@amfam/billing/payment-method/data-access/lib/+state/payment-method/payment-method.actions';
import { selectHasScheduledPaymentForBillaccount } from '@amfam/billing/schedulepayment/data-access/lib/+state/schedulepayment.selectors';
import { PaymentService } from '@amfam/billing/shared/util/lib/services/payment.service';

import { GetAutomaticPaymentsResponse } from '../models/auto-pay.models';
import { AutoPayService } from '../services/auto-pay.service';
import {
  addMultipleAutoPay,
  addMultipleAutoPayFailure,
  addMultipleAutoPayPartialSuccess,
  addMultipleAutoPaySuccess,
  applyAutoPayDiscount,
  applyAutoPayDiscountFailure,
  applyAutoPayDiscountSuccess,
  deleteAutoPay,
  deleteAutoPayFailure,
  deleteAutoPaySuccess,
  editAutoPay,
  editAutoPayFailure,
  editAutoPaySuccess,
  getAllAutomaticPayments,
  getAllAutomaticPaymentsFailure,
  getAllAutomaticPaymentsSuccess,
  getAutomaticPayment,
  getAutomaticPaymentFailure,
  getAutomaticPaymentSuccess,
  getAutoPayPrediction,
  getAutoPayPredictionFailure,
  getAutoPayPredictionSuccess,
  updateAutoPay
} from './auto-pay.actions';

export const getAutomaticPayments = createEffect(
  (actions$ = inject(Actions), autoPayService = inject(AutoPayService)) => {
    return actions$.pipe(
      ofType(getAutomaticPayment),
      switchMap(action =>
        autoPayService.getAutomaticPayments(action.billAccountNumber).pipe(
          map(res =>
            getAutomaticPaymentSuccess({
              response: res,
              billAccountNumber: action.billAccountNumber,
              correlationId: action.correlationId
            })
          ),
          catchError(error =>
            of(
              getAutomaticPaymentFailure({
                error,
                billAccountNumber: action.billAccountNumber,
                correlationId: action.correlationId
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

export const getAllAutomaticPaymentsEffect = createEffect(
  (actions$ = inject(Actions), autoPayService = inject(AutoPayService)) => {
    return actions$.pipe(
      ofType(getAllAutomaticPayments),
      switchMap(action => {
        const responses: Observable<GetAutomaticPaymentsResponse>[] = action.billAccountNumbers.map(
          billAccountNumber => autoPayService.getAutomaticPayments(billAccountNumber)
        );
        return forkJoin(responses).pipe(
          map((res: GetAutomaticPaymentsResponse[]) =>
            getAllAutomaticPaymentsSuccess({
              response: res,
              correlationId: action.correlationId
            })
          ),
          catchError(error =>
            of(
              getAllAutomaticPaymentsFailure({
                error,
                billAccountNumbers: action.billAccountNumbers,
                correlationId: action.correlationId
              })
            )
          )
        );
      })
    );
  },
  { functional: true }
);

export const getAutoPayPredictionEffect = createEffect(
  (actions$ = inject(Actions), autoPayService = inject(AutoPayService)) => {
    return actions$.pipe(
      ofType(getAutoPayPrediction),
      switchMap(action =>
        autoPayService.getAutoPayPrediction(action.payload, action.context).pipe(
          map(res =>
            getAutoPayPredictionSuccess({
              response: res,
              correlationId: action.correlationId
            })
          ),
          catchError(error =>
            of(
              getAutoPayPredictionFailure({
                error,
                correlationId: action.correlationId
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

export const editAutoPayEffect = createEffect(
  (
    actions$ = inject(Actions),
    autoPayService = inject(AutoPayService),
    paymentService = inject(PaymentService)
  ) => {
    return actions$.pipe(
      ofType(editAutoPay),
      switchMap(action =>
        autoPayService.editAutoPay(action.payload, action.billAccountNumber).pipe(
          map(res =>
            editAutoPaySuccess({
              response: res,
              correlationId: action.correlationId,
              confirmation: paymentService.buildEditAutomaticPaymentConfirmationData(
                action.payload,
                res.autoPayRules
              )
            })
          ),
          catchError(error =>
            of(
              editAutoPayFailure({
                error,
                billAccountNumber: action.billAccountNumber,
                correlationId: action.correlationId
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

export const deleteAutoPayEffect = createEffect(
  (
    actions$ = inject(Actions),
    autoPayService = inject(AutoPayService),
    paymentService = inject(PaymentService),
    store = inject(Store)
  ) => {
    return actions$.pipe(
      ofType(deleteAutoPay),
      switchMap(action =>
        autoPayService.deleteAutoPay(action.payload).pipe(
          withLatestFrom(
            store.select(
              selectHasScheduledPaymentForBillaccount(action.payload.billAccountNumber ?? '')
            )
          ),
          map(([res, hasScheduledPayment]) =>
            deleteAutoPaySuccess({
              response: res,
              billAccountNumber: action.payload.billAccountNumber ?? '',
              correlationId: action.correlationId,
              confirmation: paymentService.buildDeleteAutomaticPaymentConfirmationData(
                action.payload,
                res,
                hasScheduledPayment
              )
            })
          ),
          catchError(error =>
            of(
              deleteAutoPayFailure({
                error,
                billAccountNumber: action.payload.billAccountNumber ?? '',
                correlationId: action.correlationId
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

// When payment method is edited we need to make sure any payment references are updated
export const editPaymentMethodSuccess = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(PaymentMethodActionsEditPaymentMethodSuccess),
      map(action => {
        const payload = {
          newNickName: action.newNickName,
          oldNickName: action.oldNickName
        };
        return updateAutoPay({ payload });
      })
    );
  },
  { functional: true }
);

export const addMultipleAutoPayEffect = createEffect(
  (
    actions$ = inject(Actions),
    autoPayService = inject(AutoPayService),
    paymentService = inject(PaymentService)
  ) => {
    return actions$.pipe(
      ofType(addMultipleAutoPay),
      switchMap(action =>
        autoPayService.addMultipleAutoPay(action.payload).pipe(
          map(response => {
            const success: boolean = (response.autoPayRules ?? []).length > 0;
            const failure: boolean = (response.partialStatuses ?? []).length > 0;

            return success && !failure
              ? addMultipleAutoPaySuccess({
                  response,
                  correlationId: action.correlationId,
                  req: action.payload,
                  confirmation: paymentService.buildMultipleAddAutomaticPaymentConfirmationData(
                    action.payload,
                    response,
                    response.autoPayRules,
                    response.partialStatuses
                  )
                })
              : success && failure
                ? addMultipleAutoPayPartialSuccess({
                    response,
                    correlationId: action.correlationId
                  })
                : addMultipleAutoPayFailure({
                    error: response.partialStatuses[0]?.status,
                    billAccountNumbers: action.payload.accounts.map(
                      account => account.billingNumber
                    ),
                    correlationId: action.correlationId
                  });
          }),
          catchError(error =>
            of(
              addMultipleAutoPayFailure({
                error: error,
                billAccountNumbers: action.payload.accounts.map(account => account.billingNumber),
                correlationId: action.correlationId
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

export const applyAutoPayDiscountEffect = createEffect(
  (actions$ = inject(Actions), autoPayService = inject(AutoPayService)) => {
    return actions$.pipe(
      ofType(applyAutoPayDiscount),
      mergeMap(action =>
        autoPayService.applyAutoPayDiscount(action.payload).pipe(
          map(res =>
            applyAutoPayDiscountSuccess({
              response: res
            })
          ),
          catchError(error =>
            of(
              applyAutoPayDiscountFailure({
                error
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);
